import './styles.scss';

import Provider, { ProviderMethods, ProviderProps } from '@/common/Provider';
import { getClasses, getCookies, isNil, logout } from '@/utils';

import CatchError from '@/components/CatchError';
import Header from '@/components/Header';
import ModalContainer from '@/components/ModalContainer';
import { useEffect } from 'react';
import useSession from '@/state/session';

/* Feature
  This component is really just a wrapper for the Provider component. It's main
  purpose is to decouple features from our context provider, so that in the future
  if we decide to change to a different state management solution, we can do so without
  having to change everything else that is using Provider.

  Basically just an extension to keep our features isolated from the rest of the app.
*/

export interface FeatureProps<T = unknown, M = ProviderMethods> extends Omit<ProviderProps<T, M>, 'value'> {
  name: string;
  context?: T;
  error?: (props) => JSX.Element;
  restricted?: boolean;
}

const Feature = ({
  name,
  context,
  methods,
  error: ErrorDisplay,
  children,
  restricted,
  ...providerProps
}: FeatureProps<typeof context, typeof methods>): JSX.Element => {
  useEffect((): void => {
    if (restricted === undefined || restricted === false) return;
    const user = useSession.getState().state.user;
    const { token } = getCookies('token');
    if (!token || isNil(user)) logout();
  }, [restricted]);

  return (
    <>
      <Header />
      <Provider {...providerProps} value={context} methods={methods}>
        <main className={getClasses('Feature', name.replace(/\s/g, ''))}>
          <CatchError fallback={ErrorDisplay}>{children}</CatchError>
        </main>
        <ModalContainer />
      </Provider>
    </>
  );
};

export default Feature;
